import axios from 'axios'
import config from '../config/config.js'

// Create axios instance with default configuration
const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('neat_fit_auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('neat_fit_auth_token')
      localStorage.removeItem('neat_fit_refresh_token')
      localStorage.removeItem('neat_fit_user_data')

      // Signal the app to perform a graceful React-Router-based logout
      // rather than a hard browser redirect, so components can render error
      // states and the redirect goes through ProtectedLayout normally.
      if (window.location.pathname !== '/auth/login') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
      }
    }

    return Promise.reject(error)
  }
)

const parseStoredJson = (value) => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const resolveCompanyUrl = () => {
  const userData = parseStoredJson(localStorage.getItem(config.storage.userData))
  if (userData?.company_url) return userData.company_url

  const companyData = parseStoredJson(localStorage.getItem(config.storage.companyData))
  if (companyData?.company?.company_url) return companyData.company.company_url
  if (companyData?.company_url) return companyData.company_url

  const authStorage = parseStoredJson(localStorage.getItem('auth-storage'))
  if (authStorage?.state?.companyUrl) return authStorage.state.companyUrl

  return ''
}

const formatApiError = (error, fallback) => {
  return error.response?.data?.message || error.response?.data?.error || error.message || fallback
}

const requestWithResult = async (method, endpoint, data = undefined, requestConfig = {}, fallback = 'Request failed') => {
  try {
    const response = await api({
      method,
      url: endpoint,
      data,
      ...requestConfig,
    })

    return {
      success: true,
      data: response.data,
    }
  } catch (error) {
    return {
      success: false,
      error: formatApiError(error, fallback),
    }
  }
}

// API service methods
export const apiService = {
  getCompanyUrl() {
    return resolveCompanyUrl()
  },

  withCompanyEndpoint(path, companyUrl = null) {
    const resolvedCompanyUrl = companyUrl || resolveCompanyUrl()
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    if (!resolvedCompanyUrl) return normalizedPath
    return `/${resolvedCompanyUrl}${normalizedPath}`
  },

  // Food template uploader APIs
  fetchFoodTemplateCategories(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/foods/getfoodtemplatecategories', companyUrl),
      undefined,
      {},
      'Failed to load food categories'
    )
  },

  fetchFoodTemplates(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/foods/getfoodtemplates', companyUrl),
      payload,
      {},
      'Failed to load food templates'
    )
  },

  fetchFoodTemplatesPerCategory(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/foods/getfoodtemplatespercategory', companyUrl),
      undefined,
      {},
      'Failed to load food category mapping'
    )
  },

  fetchFoodTemplateDetails(companyUrl = null, templateId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/foods/getfoodtemplatedetails/${templateId}`, companyUrl),
      undefined,
      {},
      'Failed to load food template details'
    )
  },

  createFoodTemplate(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/foods/createfoodtemplate', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create food template'
    )
  },

  updateFoodTemplate(companyUrl = null, templateId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/foods/updatefoodtemplate/${templateId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update food template'
    )
  },

  deleteFoodTemplates(companyUrl = null, templateIds = []) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/foods/deletefoodtemplate', companyUrl),
      { template_ids: templateIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete food templates'
    )
  },

  createFoodTemplateCategory(companyUrl = null) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/foods/createfoodtempcategory', companyUrl),
      {},
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create food category'
    )
  },

  updateFoodTemplateCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/foods/updatefoodtempcategory/${categoryId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update food category'
    )
  },

  deleteFoodTemplateCategory(companyUrl = null, categoryId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/foods/deletefoodtempcategory/${categoryId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete food category'
    )
  },

  uploadFoodTemplateAssets(companyUrl = null, templateId, { images = [], recipeFile = null } = {}) {
    const formData = new FormData()

    images.forEach((file) => {
      formData.append('images', file)
    })

    if (recipeFile) {
      formData.append('recipepdf', recipeFile)
    }

    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/foods/uploadimages/${templateId}`, companyUrl),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Template saved but asset upload failed'
    )
  },

  // Exercise template uploader APIs
  fetchExerciseTemplateCategories(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/exercises/getexercisetempcategory/', companyUrl),
      undefined,
      {},
      'Failed to load exercise categories'
    )
  },

  fetchExerciseTemplates(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/exercises/getexercisetemplates/', companyUrl),
      payload,
      {},
      'Failed to load exercise templates'
    )
  },

  fetchExerciseTemplatesPerCategory(companyUrl = null, categoryId = null) {
    const suffix = categoryId && `${categoryId}` !== '-1' ? `/${categoryId}` : '/'
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/exercises/getexercisetempspercategory${suffix}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load exercise category mapping'
    )
  },

  fetchExerciseTemplateDetails(companyUrl = null, templateId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/exercises/getexercisetemplatedetails/${templateId}`, companyUrl),
      undefined,
      {},
      'Failed to load exercise template details'
    )
  },

  createExerciseTemplate(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/exercises/createexercisetemplate', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create exercise template'
    )
  },

  updateExerciseTemplate(companyUrl = null, templateId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exercises/updateexercisetemplate/${templateId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update exercise template'
    )
  },

  deleteExerciseTemplates(companyUrl = null, templateIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint('/exercises/deleteexercisetemplate', companyUrl),
      { exercisetemplate_ids: templateIds },
      {},
      'Failed to delete exercise templates'
    )
  },

  updateExerciseTemplateCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exercises/updateexercisetempcategory/${categoryId}`, companyUrl),
      payload,
      {},
      'Failed to update exercise category'
    )
  },

  deleteExerciseTemplateCategory(companyUrl = null, categoryId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/exercises/deleteexercisetempcategory/${categoryId}`, companyUrl),
      undefined,
      {},
      'Failed to delete exercise category'
    )
  },

  uploadExerciseTemplateAssets(companyUrl = null, templateId, { images = [] } = {}) {
    const formData = new FormData()

    images.forEach((file) => {
      formData.append('sequence', file)
    })

    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exercises/uploadimages/${templateId}`, companyUrl),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Exercise saved but image upload failed'
    )
  },

  // Recipe template uploader APIs
  fetchRecipeCategories(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/recipe/getrecipecategory', companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load recipe categories'
    )
  },

  fetchRecipes(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/recipe/getrecipes', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load recipes'
    )
  },

  createRecipe(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/recipe/createrecipe', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create recipe'
    )
  },

  updateRecipe(companyUrl = null, recipeId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/recipe/updaterecipe/${recipeId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update recipe'
    )
  },

  deleteRecipes(companyUrl = null, recipeIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint('/recipe/deleterecipes', companyUrl),
      { recipe_ids: recipeIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete recipes'
    )
  },

  fetchRecipeDetails(companyUrl = null, recipeId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/recipe/getrecipedetails/${recipeId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load recipe details'
    )
  },

  updateRecipeCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/recipe/updaterecipecategory/${categoryId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update recipe category'
    )
  },

  deleteRecipeCategory(companyUrl = null, categoryId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/recipe/deleterecipecategory/${categoryId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete recipe category'
    )
  },

  fetchRecipesPerCategory(companyUrl = null, categoryId = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(
        `/recipe/getrecipespercategory/${categoryId ? `${categoryId}` : ''}`,
        companyUrl
      ),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load recipe category layout'
    )
  },

  // Meal template uploader APIs
  fetchMealTemplateCategories(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/meals/getmealtemplatecategory/', companyUrl),
      undefined,
      {},
      'Failed to load meal categories'
    )
  },

  fetchMealTemplates(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/meals/getmealtemplates', companyUrl),
      payload,
      {},
      'Failed to load meal templates'
    )
  },

  fetchMealTemplatesPerCategory(companyUrl = null, categoryId = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(
        `/meals/getmealtemplatespercategory/${categoryId ? `${categoryId}` : ''}`,
        companyUrl
      ),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load meal category mapping'
    )
  },

  fetchMealTemplateFoods(companyUrl = null, templateId, foodId = null) {
    const suffix = foodId ? `/${foodId}` : ''
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/meals/getmealtemplatefoods/${templateId}${suffix}`, companyUrl),
      undefined,
      {},
      'Failed to load meal template foods'
    )
  },

  createMealTemplate(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/meals/createmealtemplate', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create meal template'
    )
  },

  updateMealTemplate(companyUrl = null, templateId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/meals/updatemealtemplate/${templateId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update meal template'
    )
  },

  updateMealTemplateCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/meals/updatemealtemplatecategory/${categoryId}`, companyUrl),
      payload,
      {},
      'Failed to update meal category'
    )
  },

  deleteMealTemplates(companyUrl = null, mealIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint('/meals/deletemealtemplates', companyUrl),
      { meal_ids: mealIds },
      {},
      'Failed to delete meal templates'
    )
  },

  deleteMealTemplateFoods(companyUrl = null, mealTemplateId, foodIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/meals/deletemealsfoods/${mealTemplateId}`, companyUrl),
      { food_ids: foodIds },
      {},
      'Failed to delete meal template foods'
    )
  },

  addFoodToMealTemplate(companyUrl = null, mealTemplateId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/meals/addfoodtomealtemplate/${mealTemplateId}`, companyUrl),
      payload,
      {},
      'Failed to add food to meal template'
    )
  },

  // Nutrition program template uploader APIs
  fetchNutritionProgramCategories(companyUrl = null, categoryId = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(
        `/nutritionprograms/getnutritiontemplatespercategory/${categoryId ? `${categoryId}` : ''}`,
        companyUrl
      ),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load nutrition program categories'
    )
  },

  fetchNutritionProgramTree(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/getprogramnutritiontabtree', companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load nutrition program library'
    )
  },

  fetchNutritionPrograms(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/nutritionprograms/getnutritionprograms', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load nutrition programs'
    )
  },

  fetchNutritionProgramMealPlans(companyUrl = null, programId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/nutritionprograms/getnutritionprogrammealplans/${programId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load nutrition program details'
    )
  },

  createNutritionProgram(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/nutritionprograms/createnutritionprogram', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create nutrition program'
    )
  },

  updateNutritionProgram(companyUrl = null, programId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/updatenutritionprogram/${programId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update nutrition program'
    )
  },

  updateNutritionProgramMealPlan(companyUrl = null, programId, mealPlanId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/updatenutritionprogrammealplan/${programId}/${mealPlanId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update nutrition program meal plan'
    )
  },

  updateNutritionProgramMealFood(companyUrl = null, mealId, mealFoodId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/updatenutritionprogrammealfood/${mealId}/${mealFoodId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update nutrition program meal food'
    )
  },

  updateNutritionProgramCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/updatecategory/${categoryId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update nutrition program category'
    )
  },

  deleteNutritionPrograms(companyUrl = null, programIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint('/nutritionprograms/deletenutritionprograms', companyUrl),
      { nutritionprogram_ids: programIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete nutrition programs'
    )
  },

  deleteNutritionProgramMealPlans(companyUrl = null, programId, mealPlanIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/deletenutritionprogrammealplans/${programId}`, companyUrl),
      { nutritionprogrammealplan_ids: mealPlanIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete nutrition program meal plans'
    )
  },

  deleteNutritionProgramMealFoods(companyUrl = null, mealId, foodIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/nutritionprograms/deletenutritionprogrammealfoods/${mealId}`, companyUrl),
      { food_ids: foodIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete nutrition program meal foods'
    )
  },

  searchNewNutrition(companyUrl, query, signal = null) {
    return this.get(
      this.withCompanyEndpoint(`/nutrition/search/${encodeURIComponent(query)}`, companyUrl),
      {},
      signal
    )
  },

  fetchNutritionWithDetails(companyUrl, item, signal = null) {
    const itemName = typeof item === 'object' ? (item.item_name || item.name) : item
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/nutrition/nutrientswithdetails', companyUrl),
      { item: itemName },
      {
        signal,
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load nutrition details'
    )
  },

  // Exercise plan template uploader APIs
  fetchExercisePlanTemplateCategories(companyUrl = null, categoryId = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(
        `/exerciseplans/getexerciseplantemplatespercategory/${categoryId ? `${categoryId}` : ''}`,
        companyUrl
      ),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load exercise plan template categories'
    )
  },

  fetchExercisePlanTemplateTree(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/exercise/getexercisecategorytabtree', companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load exercise plan template library'
    )
  },

  fetchExercisePlanTemplates(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/exerciseplans/getexerciseplantemplates', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load exercise plan templates'
    )
  },

  fetchExercisePlanTemplateDetails(companyUrl = null, templateId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/exerciseplans/getexerciseplantemplates/${templateId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to load exercise plan template details'
    )
  },

  createExercisePlanTemplate(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/exerciseplans/createexerciseplantemplate', companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to create exercise plan template'
    )
  },

  updateExercisePlanTemplate(companyUrl = null, templateId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exerciseplans/updateexerciseplantemplate/${templateId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update exercise plan template'
    )
  },

  updateExercisePlanTemplateExercise(companyUrl = null, planId, exerciseId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exerciseplans/updateexerciseplanexercise/${planId}/${exerciseId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update exercise plan template exercise'
    )
  },

  updateExercisePlanTemplatePlan(companyUrl = null, templateId, planId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exerciseplans/updateexerciseplantemplateplan/${templateId}/${planId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update exercise plan template plan'
    )
  },

  updateExercisePlanTemplateCategory(companyUrl = null, categoryId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exerciseplans/updateexerciseplantemplatecategory/${categoryId}`, companyUrl),
      payload,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to update exercise plan template category'
    )
  },

  deleteExercisePlanTemplateCategory(companyUrl = null, categoryId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/exerciseplans/deleteexerciseplantemplatecategory/${categoryId}`, companyUrl),
      undefined,
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete exercise plan template category'
    )
  },

  deleteExercisePlanTemplatePlanExercises(companyUrl = null, planId, exerciseIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/exerciseplans/deleteexerciseplantemplateplanexercises/${planId}`, companyUrl),
      { exercise_ids: exerciseIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete exercise plan template exercises'
    )
  },

  deleteExercisePlanTemplates(companyUrl = null, templateIds = []) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint('/exerciseplans/deleteexerciseplantemplates', companyUrl),
      { template_ids: templateIds },
      {
        headers: {
          'loguierrorkey': '9Z$rJaF^J>6P?VHzc039emw$uTUfXKHFd3',
        },
      },
      'Failed to delete exercise plan templates'
    )
  },

  deleteExerciseTemplateImage(companyUrl = null, templateId, imageId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/exercises/deleteexercisetemplateimage/${templateId}/${imageId}`, companyUrl),
      undefined,
      {},
      'Failed to delete exercise image'
    )
  },

  // Super admin APIs
  fetchCompanies(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/companyadmin/getcompanies', companyUrl),
      undefined,
      {},
      'Failed to load companies'
    )
  },

  createCompany(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/companyadmin/createcompany', companyUrl),
      payload,
      {},
      'Failed to create company'
    )
  },

  createCompanyAdmin(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/superadmin/createcompanyadmin', companyUrl),
      payload,
      {},
      'Failed to create company admin'
    )
  },

  deleteCompany(companyUrl = null, companyId) {
    return requestWithResult(
      'delete',
      this.withCompanyEndpoint(`/companyadmin/deletecompany/${companyId}`, companyUrl),
      undefined,
      {},
      'Failed to delete company'
    )
  },

  reindexCompanyData(companyUrl = null, companyId) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/superadmin/reindexdata/${companyId}`, companyUrl),
      {},
      {},
      'Failed to rebuild indices'
    )
  },

  updateComplianceSettings(companyUrl = null, settingsId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/superadmin/updatecompliancesettings/${settingsId}`, companyUrl),
      payload,
      {},
      'Failed to update compliance settings'
    )
  },

  // Billing & subscription APIs
  fetchBillingPlans(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/billing/plans', companyUrl),
      undefined,
      {},
      'Failed to load subscription plans'
    )
  },

  fetchCurrentSubscription(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/billing/subscription', companyUrl),
      undefined,
      {},
      'Failed to load current subscription'
    )
  },

  fetchBillingHistory(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/billing/history', companyUrl),
      undefined,
      {},
      'Failed to load billing history'
    )
  },

  createBillingCheckoutSession(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/billing/create-checkout-session', companyUrl),
      payload,
      {},
      'Failed to start checkout'
    )
  },

  cancelBillingSubscription(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/billing/cancel-subscription', companyUrl),
      payload,
      {},
      'Failed to cancel subscription'
    )
  },

  // Roles & permissions APIs
  fetchRoles(companyUrl = null, filterCompanyId = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/permission/roles', companyUrl),
      undefined,
      filterCompanyId ? { params: { company_id: filterCompanyId } } : {},
      'Failed to load roles'
    )
  },

  fetchPermissionItems(companyUrl = null) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint('/permissionitems', companyUrl),
      undefined,
      {},
      'Failed to load permissions'
    )
  },

  createRole(companyUrl = null, payload = {}) {
    return requestWithResult(
      'post',
      this.withCompanyEndpoint('/permission/createrole', companyUrl),
      payload,
      {},
      'Failed to create role'
    )
  },

  fetchPermissionPrivileges(companyUrl = null, roleId) {
    return requestWithResult(
      'get',
      this.withCompanyEndpoint(`/permission/permissionprivileges/${roleId}`, companyUrl),
      undefined,
      {},
      'Failed to load role permissions'
    )
  },

  updatePermissionPrivilege(companyUrl = null, roleId, payload = {}) {
    return requestWithResult(
      'put',
      this.withCompanyEndpoint(`/permission/updatepermissionprivileges/${roleId}`, companyUrl),
      payload,
      {},
      'Failed to update permission'
    )
  },

  // Signup APIs
  async checkEmailAvailability(email) {
    try {
      const response = await api.get(`${config.api.endpoints.checkEmailAvailability}/${encodeURIComponent(email)}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to check email'
      }
    }
  },

  async checkCompanyUrlAvailability(companyUrl) {
    try {
      const response = await api.get(`${config.api.endpoints.checkCompanyUrl}/${encodeURIComponent(companyUrl)}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to check company URL'
      }
    }
  },

  async signupCompany(payload) {
    try {
      const response = await api.post(config.api.endpoints.signup, payload)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      const responseData = error.response?.data
      // Extract first validation error message if present
      const validationError = responseData?.errors?.[0]?.msg || responseData?.errors?.[0]?.message
      return {
        success: false,
        error: validationError || responseData?.message || responseData?.error || error.message || 'Signup failed'
      }
    }
  },

  // Authentication APIs
  async fetchUserCompanies(email) {
    try {
      const response = await api.get(`${config.api.endpoints.fetchUserCompanies}/${email}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch companies'
      }
    }
  },

  async login(credentials) {
    try {
      const response = await api.post(config.api.endpoints.auth, credentials)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      // /auth answers failures as { "error": "Invalid email or password" },
      // or as { "errors": "..." } / { "errors": ["..."] } for missing fields / unknown users
      const body = error.response?.data
      const errors = Array.isArray(body?.errors) ? body.errors[0] : body?.errors
      return {
        success: false,
        error: [body?.error, body?.message, errors].find((v) => typeof v === 'string' && v) || error.message || 'Login failed'
      }
    }
  },

  async loginWithGoogle(payload) {
    try {
      const response = await api.post(config.api.endpoints.googleAuth, payload)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.errors?.[0] || error.response?.data?.error || error.message || 'Google login failed'
      }
    }
  },

  async fetchSideMenu(companyUrl) {
    try {
      const response = await api.get(`/${companyUrl}${config.api.endpoints.sideMenu}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch side menu'
      }
    }
  },

  async fetchCompanyDetails(companyUrl) {
    try {
      const response = await api.get(`/${companyUrl}${config.api.endpoints.companyDetails}`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch company details'
      }
    }
  },

  async fetchClientDashboard(companyUrl, logdate, userId = null, signal = null) {
    try {
      const cfg = {}
      if (signal) {
        cfg.signal = signal
      }
      const payload = { logdate }
      if (userId) {
        payload.user_id = userId
      }
      const response = await api.post(`/${companyUrl}/clientdashboard`, payload, cfg)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch client dashboard data'
      }
    }
  },

  async fetchProgramArchive(companyUrl, signal = null) {
    try {
      const config = {}
      if (signal) {
        config.signal = signal
      }
      const response = await api.post(`/${companyUrl}/programarchive`, {}, config)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch program archive data'
      }
    }
  },

  async updateClient(companyUrl, payload, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.put(`/${companyUrl}/updateclient`, payload, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to update client' }
    }
  },

  async deleteClient(companyUrl, userId, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.delete(`/${companyUrl}/companyadmin/deleteclient/${userId}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to delete client' }
    }
  },

  async impersonateClient(companyUrl, userId, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}/companyadmin/impersonate/${userId}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to impersonate client' }
    }
  },

  async fetchStaticPages(companyUrl, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.staticPages}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to fetch static pages' }
    }
  },

  async deleteLogNutritionFood(companyUrl, planId, foodId, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.delete(`/${companyUrl}${config.api.endpoints.deleteLogNutritionFood}/${planId}/${foodId}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Delete failed' }
    }
  },

  async getShoppingListPdf(companyUrl, programId, clientId, signal = null) {
    try {
      const cfg = { responseType: 'blob' }
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.shoppingListPdf(programId, clientId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to fetch shopping list' }
    }
  },

  async getMealsPdf(companyUrl, programId, clientId, signal = null) {
    try {
      const cfg = { responseType: 'blob' }
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.mealsPdf(programId, clientId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to fetch meals PDF' }
    }
  },

  async getRecipePdf(companyUrl, programId, signal = null) {
    try {
      const cfg = { responseType: 'blob' }
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.recipePdf(programId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to fetch recipe PDF' }
    }
  },

  async getExercisesPdf(companyUrl, clientId, sessionId, signal = null) {
    try {
      const cfg = { responseType: 'blob' }
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.exercisesPdf(clientId, sessionId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to fetch exercises PDF' }
    }
  },

  async searchNutrition(companyUrl, query, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.nutritionSearch}/${encodeURIComponent(query)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Search failed' }
    }
  },

  async searchNutritionByScan(companyUrl, id, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.get(`/${companyUrl}${config.api.endpoints.nutritionSearchByScan}/${encodeURIComponent(id)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Search by scan failed' }
    }
  },

  async fetchNutritionNutrients(companyUrl, itemName, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.post(`/${companyUrl}${config.api.endpoints.nutritionNutrients}`, { item: itemName }, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Nutrient fetch failed' }
    }
  },

  async createLogExerciseSingle(companyUrl, payload, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.post(`/${companyUrl}/logexercisesingle/create`, payload, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to save exercise log' }
    }
  },

  async createManualExerciseEntry(companyUrl, payload, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.post(`/${companyUrl}/logexercisesingle/createmanualentry`, payload, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to create manual exercise entry' }
    }
  },

  async updateLogExerciseSingle(companyUrl, payload, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.post(`/${companyUrl}/logexercisesingle/updatelogdata`, payload, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to update exercise log' }
    }
  },

  async deleteLogExerciseSingle(companyUrl, logExerciseSingleId, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.delete(`/${companyUrl}/deletelogexercisesingle/${encodeURIComponent(logExerciseSingleId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to delete exercise log' }
    }
  },

  async deleteLogExerciseSingleSet(companyUrl, logExerciseSingleSetId, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.delete(`/${companyUrl}/deletelogexercisesingleset/${encodeURIComponent(logExerciseSingleSetId)}`, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Failed to delete exercise set' }
    }
  },

  async createLogNutritionFood(companyUrl, payload, signal = null) {
    try {
      const cfg = {}
      if (signal) cfg.signal = signal
      const response = await api.post(`/${companyUrl}${config.api.endpoints.createLogNutritionFood}`, payload, cfg)
      return { success: true, data: response.data }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error
      return { success: false, error: error.response?.data?.message || error.message || 'Create log failed' }
    }
  },

  // Generic API methods with AbortController support
  async get(endpoint, params = {}, signal = null) {
    try {
      const config = { params }
      if (signal) {
        config.signal = signal
      }
      const response = await api.get(endpoint, config)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Request failed'
      }
    }
  },

  async post(endpoint, data = {}, signal = null) {
    try {
      const config = {}
      if (signal) {
        config.signal = signal
      }
      const response = await api.post(endpoint, data, config)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Request failed'
      }
    }
  },

  async put(endpoint, data = {}, signal = null) {
    try {
      const config = {}
      if (signal) {
        config.signal = signal
      }
      const response = await api.put(endpoint, data, config)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Request failed'
      }
    }
  },

  async delete(endpoint, signal = null) {
    try {
      const config = {}
      if (signal) {
        config.signal = signal
      }
      const response = await api.delete(endpoint, config)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw error // Re-throw abort errors to be handled by the caller
      }
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Request failed'
      }
    }
  }
}

export default api
